import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/shared/components/empty-state';

import { CHART_HEIGHT } from '../lib/chart-axis';

export interface LegendItem {
  label: string;
  color: string;
}

/**
 * A chart card: title, an inline legend for two-series charts (a legend box for
 * one series is furniture — the title names it), and an empty state. Cards do
 * not split across printed pages.
 */
export function ChartCard({
  title,
  description,
  legend,
  isEmpty,
  emptyTitle = 'Nothing recorded in this period',
  children,
}: {
  title: string;
  description?: string;
  legend?: readonly LegendItem[];
  isEmpty: boolean;
  emptyTitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="gap-3 print:break-inside-avoid">
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm">{title}</CardTitle>
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
        {legend && !isEmpty && (
          <ul className="flex flex-wrap gap-3 text-xs" aria-label={`${title} legend`}>
            {legend.map((item) => (
              <li key={item.label} className="flex items-center gap-1.5">
                <span
                  className="size-2.5 rounded-sm"
                  style={{ backgroundColor: item.color }}
                  aria-hidden
                />
                {item.label}
              </li>
            ))}
          </ul>
        )}
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <EmptyState title={emptyTitle} className="py-8" />
        ) : (
          <div style={{ height: CHART_HEIGHT }}>{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

/** The tooltip box the dashboard charts use. */
export function ChartTooltipBox({
  title,
  lines,
}: {
  title: string;
  lines: readonly { label: string; value: string; color?: string }[];
}) {
  return (
    <div className="bg-popover text-popover-foreground rounded-md border px-2.5 py-1.5 text-xs shadow-md">
      <p className="text-muted-foreground mb-1 text-[11px]">{title}</p>
      {lines.map((line) => (
        <p key={line.label} className="flex items-center gap-1.5 font-medium tabular-nums">
          {line.color && (
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: line.color }}
              aria-hidden
            />
          )}
          {line.value}
          <span className="text-muted-foreground font-normal">{line.label}</span>
        </p>
      ))}
    </div>
  );
}
